import { useEffect, useState, useCallback } from "react";
import { Flag, MapPin, Trophy, Loader2, Pencil, Trash2, Star, CalendarPlus } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { getAdminRaces, createRace, updateRace, deleteRace } from "@/lib/api";

const EMPTY_FORM = {
  name: "",
  series: "",
  date: "",
  location: "",
  country: "CZ",
  status: "upcoming",
  result: "",
  highlight: false,
};

const fmtDate = (iso) =>
  new Date(iso).toLocaleDateString("cs-CZ", { day: "numeric", month: "long", year: "numeric" });

const RaceFields = ({ form, setForm, idPrefix }) => {
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  return (
    <>
      <Input
        data-testid={`${idPrefix}-name-input`}
        placeholder="Název závodu *"
        value={form.name}
        onChange={set("name")}
        className="h-11 border-white/15 bg-[#0B0D10]"
      />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Input
          data-testid={`${idPrefix}-series-input`}
          placeholder="Série (např. CDS PRO)"
          value={form.series}
          onChange={set("series")}
          className="h-11 border-white/15 bg-[#0B0D10]"
        />
        <Input
          data-testid={`${idPrefix}-date-input`}
          type="date"
          value={form.date}
          onChange={set("date")}
          className="h-11 border-white/15 bg-[#0B0D10] font-mono [color-scheme:dark]"
        />
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Input
          data-testid={`${idPrefix}-location-input`}
          placeholder="Místo (např. Autodrom Most) *"
          value={form.location}
          onChange={set("location")}
          className="h-11 border-white/15 bg-[#0B0D10]"
        />
        <Input
          data-testid={`${idPrefix}-country-input`}
          placeholder="Země (CZ)"
          value={form.country}
          onChange={set("country")}
          className="h-11 border-white/15 bg-[#0B0D10]"
        />
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Select
          value={form.status}
          onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}
        >
          <SelectTrigger
            data-testid={`${idPrefix}-status-select`}
            className="h-11 border-white/15 bg-[#0B0D10]"
          >
            <SelectValue placeholder="Stav" />
          </SelectTrigger>
          <SelectContent className="border-white/10 bg-[#0F1217]">
            <SelectItem value="upcoming" data-testid={`${idPrefix}-status-option-upcoming`}>
              Nadcházející
            </SelectItem>
            <SelectItem value="done" data-testid={`${idPrefix}-status-option-done`}>
              Odjeto
            </SelectItem>
          </SelectContent>
        </Select>
        <Input
          data-testid={`${idPrefix}-result-input`}
          placeholder="Výsledek (např. 2. místo)"
          value={form.result}
          onChange={set("result")}
          disabled={form.status !== "done"}
          className="h-11 border-white/15 bg-[#0B0D10] disabled:opacity-40"
        />
      </div>
      <label className="flex cursor-pointer items-center justify-between border border-white/10 bg-[#0B0D10] px-4 py-3">
        <span className="flex items-center gap-2 text-sm text-white/70">
          <Star className="h-4 w-4 text-[#B7FF2A]" /> Zvýraznit závod
        </span>
        <Switch
          data-testid={`${idPrefix}-highlight-switch`}
          checked={form.highlight}
          onCheckedChange={(v) => setForm((f) => ({ ...f, highlight: v }))}
          className="data-[state=checked]:bg-[#B7FF2A]"
        />
      </label>
    </>
  );
};

const validate = (form) => {
  if (!form.name.trim()) return "Vyplň název závodu.";
  if (!form.date) return "Vyber datum závodu.";
  if (!form.location.trim()) return "Vyplň místo závodu.";
  return null;
};

const toPayload = (form) => ({
  name: form.name.trim(),
  series: form.series.trim(),
  date: form.date,
  location: form.location.trim(),
  country: form.country.trim() || "CZ",
  status: form.status,
  result: form.status === "done" ? form.result.trim() || null : null,
  highlight: form.highlight,
});

export const RaceManager = ({ onChanged }) => {
  const [races, setRaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);

  const [editRace, setEditRace] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_FORM);
  const [editSaving, setEditSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRaces(await getAdminRaces());
    } catch {
      // 401 handled globally
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const refresh = async () => {
    await load();
    onChanged?.();
  };

  const submit = async (e) => {
    e.preventDefault();
    const err = validate(form);
    if (err) {
      toast.error(err);
      return;
    }
    setSubmitting(true);
    try {
      await createRace(toPayload(form));
      toast.success("Závod přidán do kalendáře!");
      setForm(EMPTY_FORM);
      await refresh();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Závod se nepodařilo uložit.");
    } finally {
      setSubmitting(false);
    }
  };

  const openEdit = (race) => {
    setEditRace(race);
    setEditForm({
      name: race.name,
      series: race.series || "",
      date: race.date,
      location: race.location,
      country: race.country || "CZ",
      status: race.status,
      result: race.result || "",
      highlight: !!race.highlight,
    });
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    const err = validate(editForm);
    if (err) {
      toast.error(err);
      return;
    }
    setEditSaving(true);
    try {
      await updateRace(editRace.id, toPayload(editForm));
      toast.success("Změny uloženy.");
      setEditRace(null);
      await refresh();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Změny se nepodařilo uložit.");
    } finally {
      setEditSaving(false);
    }
  };

  const remove = async (raceId) => {
    try {
      await deleteRace(raceId);
      toast.success("Závod smazán.");
      await refresh();
    } catch {
      toast.error("Závod se nepodařilo smazat.");
    }
  };

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
      {/* create form */}
      <div className="lg:col-span-5">
        <form
          data-testid="admin-race-form"
          onSubmit={submit}
          className="clip-corner space-y-4 border border-white/10 bg-[#0F1217] p-6"
        >
          <div className="flex items-center gap-3">
            <div className="clip-corner-sm flex h-10 w-10 items-center justify-center bg-[#B7FF2A]/10 text-[#B7FF2A]">
              <CalendarPlus className="h-5 w-5" />
            </div>
            <div>
              <div className="font-display text-xl uppercase tracking-[0.06em]">Přidat závod</div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">
                Sezóna se určí automaticky podle data
              </div>
            </div>
          </div>

          <RaceFields form={form} setForm={setForm} idPrefix="admin-race" />

          <Button
            type="submit"
            disabled={submitting}
            data-testid="admin-race-submit-button"
            className="light-sweep clip-corner-sm h-12 w-full bg-[#B7FF2A] font-display text-base uppercase tracking-[0.14em] text-[#07080A] hover:bg-[#B7FF2A] hover:brightness-110 disabled:opacity-60"
          >
            {submitting ? (
              <span className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" /> Ukládám…
              </span>
            ) : (
              "Přidat do kalendáře"
            )}
          </Button>
        </form>
      </div>

      {/* race list */}
      <div className="lg:col-span-7">
        {loading ? (
          <div className="space-y-3" data-testid="admin-races-loading">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="clip-corner-sm h-20 animate-pulse border border-white/5 bg-[#0F1217]" />
            ))}
          </div>
        ) : races.length === 0 ? (
          <div className="py-14 text-center text-white/40" data-testid="admin-races-empty">
            Kalendář je prázdný. Přidej první závod.
          </div>
        ) : (
          <div className="space-y-3" data-testid="admin-races-list">
            {races.map((r) => (
              <div
                key={r.id}
                data-testid={`admin-race-row-${r.id}`}
                className="clip-corner-sm group flex flex-col gap-3 border border-white/10 bg-[#0F1217] p-4 transition-colors duration-200 hover:border-[#1EA7FF]/40 sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-display text-lg uppercase leading-tight tracking-[0.04em]">
                      {r.name}
                    </span>
                    {r.highlight && <Star className="h-4 w-4 shrink-0 fill-[#B7FF2A] text-[#B7FF2A]" />}
                    <Badge className="rounded-sm border border-white/15 bg-white/5 font-mono text-[10px] uppercase tracking-[0.14em] text-white/60 hover:bg-white/5">
                      {r.season}
                    </Badge>
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-white/50">
                    <span className="font-mono text-[#B7FF2A]">{fmtDate(r.date)}</span>
                    <span className="flex items-center gap-1">
                      <MapPin className="h-3 w-3" /> {r.location} · {r.country}
                    </span>
                    {r.series && <span>{r.series}</span>}
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {r.status === "done" ? (
                    <span className="inline-flex items-center gap-1.5 bg-[#1EA7FF]/10 px-2.5 py-1 font-mono text-xs font-semibold uppercase tracking-[0.08em] text-[#1EA7FF]">
                      <Trophy className="h-3.5 w-3.5" /> {r.result || "Odjeto"}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 bg-[#B7FF2A]/10 px-2.5 py-1 font-mono text-xs font-semibold uppercase tracking-[0.08em] text-[#B7FF2A]">
                      <Flag className="h-3.5 w-3.5" /> Nadcházející
                    </span>
                  )}
                  <button
                    data-testid={`admin-race-edit-${r.id}`}
                    onClick={() => openEdit(r)}
                    className="flex h-8 w-8 items-center justify-center rounded-md bg-white/5 text-white/60 transition-colors duration-200 hover:bg-[#1EA7FF] hover:text-[#07080A]"
                    aria-label="Upravit závod"
                    title="Upravit"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button
                    data-testid={`admin-race-delete-${r.id}`}
                    onClick={() => remove(r.id)}
                    className="flex h-8 w-8 items-center justify-center rounded-md bg-white/5 text-white/60 transition-colors duration-200 hover:bg-[#FF3B3B] hover:text-white"
                    aria-label="Smazat závod"
                    title="Smazat"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* edit dialog */}
      <Dialog open={!!editRace} onOpenChange={(open) => !open && setEditRace(null)}>
        <DialogContent
          data-testid="admin-race-edit-dialog"
          aria-describedby={undefined}
          className="max-w-lg border-white/10 bg-[#0F1217]"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display text-2xl uppercase tracking-[0.08em] text-white">
              <Pencil className="h-5 w-5 text-[#1EA7FF]" /> Upravit závod
            </DialogTitle>
          </DialogHeader>
          {editRace && (
            <form onSubmit={saveEdit} className="space-y-4">
              <RaceFields form={editForm} setForm={setEditForm} idPrefix="admin-race-edit" />
              <div className="flex gap-3 pt-1">
                <Button
                  type="button"
                  variant="outline"
                  data-testid="admin-race-edit-cancel-button"
                  onClick={() => setEditRace(null)}
                  className="clip-corner-sm flex-1 border-white/20 font-display uppercase tracking-[0.12em] hover:bg-white/5"
                >
                  Zrušit
                </Button>
                <Button
                  type="submit"
                  disabled={editSaving}
                  data-testid="admin-race-edit-save-button"
                  className="light-sweep clip-corner-sm flex-1 bg-[#1EA7FF] font-display uppercase tracking-[0.12em] text-[#07080A] hover:bg-[#1EA7FF] hover:brightness-110 disabled:opacity-60"
                >
                  {editSaving ? (
                    <span className="flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" /> Ukládám…
                    </span>
                  ) : (
                    "Uložit změny"
                  )}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
